import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Space,
  Tag, message, Tooltip, Typography, Badge, Tabs,
} from 'antd';
import {
  EditOutlined, ReloadOutlined, UserOutlined, TeamOutlined,
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

const { Text } = Typography;
const { TextArea } = Input;
const BASE = process.env.REACT_APP_API_URL;

interface AwardRecord {
  id: number;
  approvalId: string;
  rewardName: string;
  nomineeName: string;
  nomineeEmail: string;
  nomineeEmployeeId: string;
  nomineeLegalEntityName: string;
  nomineeCountry: string;
  nomineeCity: string;
  nomineeDesignation: string;
  nomineeGrade: string;
  nomineeUnit: string;
  nomineeBu: string;
  nomineeProductName: string;
  teamName: string;
  teamMembers: string;
  nominatorName: string;
  nominatorEmail: string;
  nominatorEmployeeId: string;
  nominatorLegalEntityName: string;
  nominatorCountry: string;
  nominatorCity: string;
  nominatorDesignation: string;
  nominatorGrade: string;
  nominatorUnit: string;
  nominatorProductName: string;
  nominatorCitation: string;
  nominatorComments: string;
  managerName: string;
  managerId: string;
  managerEmail: string;
  managerComments: string;
  managerVote: string;
  firstLevelApproverName: string;
  firstLevelApproverEmployeeId: string;
  firstLevelApproverEmail: string;
  firstLevelApproverUnit: string;
  firstLevelApproverComments: string;
  firstLevelApproverVote: string;
  secondLevelApproverName: string;
  secondLevelApproverEmailId: string;
  secondLevelApproverComments: string;
  secondLevelApproverVote: string;
  eltAverageScore: string;
  finalLevelApproverName: string;
  finalLevelApproverEmail: string;
  finalLevelApproverComments: string;
  finalLevelApproverVote: string;
  formFields: string;
  campaignName: string;
  elt1Name: string; elt1Email: string; elt1Response: string; elt1Score: string;
  elt2Name: string; elt2Email: string; elt2Response: string; elt2Score: string;
  elt3Name: string; elt3Email: string; elt3Response: string; elt3Score: string;
  elt4Name: string; elt4Email: string; elt4Response: string; elt4Score: string;
  elt5Name: string; elt5Email: string; elt5Response: string; elt5Score: string;
  elt6Name: string; elt6Email: string; elt6Response: string; elt6Score: string;
  elt7Name: string; elt7Email: string; elt7Response: string; elt7Score: string;
  elt8Name: string; elt8Email: string; elt8Response: string; elt8Score: string;
  elt9Name: string; elt9Email: string; elt9Response: string; elt9Score: string;
  elt10Name: string; elt10Email: string; elt10Response: string; elt10Score: string;
  elt11Name: string; elt11Email: string; elt11Response: string; elt11Score: string;
  elt12Name: string; elt12Email: string; elt12Response: string; elt12Score: string;
}

const voteColor = (vote: string) => {
  if (vote === 'Approved') return 'success';
  if (vote === 'Rejected') return 'error';
  if (vote === 'On Hold') return 'warning';
  return 'default';
};

const G: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>{children}</div>
);

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{
    background: '#8629A4', color: '#fff', fontWeight: 600, fontSize: 13,
    padding: '6px 12px', borderRadius: 4, marginBottom: 12,
  }}>{title}</div>
);

const VOTE_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'On Hold', label: 'On Hold' },
  { value: 'Not Reviewed', label: 'Not Reviewed' },
];

const ELT_RESPONSE_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'Support', label: 'Support' },
  { value: 'Reject', label: 'Reject' },
  { value: 'No Visibility', label: 'No Visibility' },
];

const AceAviaAwards: React.FC = () => {
  const { instance, accounts } = useMsal();

  const [records, setRecords]         = useState<AwardRecord[]>([]);
  const [filtered, setFiltered]       = useState<AwardRecord[]>([]);
  const [loading, setLoading]         = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editing, setEditing]         = useState<AwardRecord | null>(null);
  const [saving, setSaving]           = useState(false);
  const [pageSize, setPageSize]       = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch]           = useState('');
  const [rewardFilter, setRewardFilter] = useState<string>('');
  const [form] = Form.useForm();

  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return res.idToken;
  }, [instance, accounts]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const token = await fetchToken();
      const res = await fetch(`${BASE}/api/nominations/awards/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const s = (v: any) => v ?? '';
      const rows: AwardRecord[] = (data.responseData ?? data ?? []).map((r: any) => ({
        id: r.id,
        approvalId: s(r.approvalId), rewardName: s(r.rewardName),
        nomineeName: s(r.nomineeName), nomineeEmail: s(r.nomineeEmail),
        nomineeEmployeeId: s(r.nomineeEmployeeId), nomineeLegalEntityName: s(r.nomineeLegalEntityName),
        nomineeCountry: s(r.nomineeCountry), nomineeCity: s(r.nomineeCity),
        nomineeDesignation: s(r.nomineeDesignation), nomineeGrade: s(r.nomineeGrade),
        nomineeUnit: s(r.nomineeUnit), nomineeBu: s(r.nomineeBu), nomineeProductName: s(r.nomineeProductName),
        teamName: s(r.teamName), teamMembers: s(r.teamMembers),
        nominatorName: s(r.nominatorName), nominatorEmail: s(r.nominatorEmail),
        nominatorEmployeeId: s(r.nominatorEmployeeId), nominatorLegalEntityName: s(r.nominatorLegalEntityName),
        nominatorCountry: s(r.nominatorCountry), nominatorCity: s(r.nominatorCity),
        nominatorDesignation: s(r.nominatorDesignation), nominatorGrade: s(r.nominatorGrade),
        nominatorUnit: s(r.nominatorUnit), nominatorProductName: s(r.nominatorProductName),
        nominatorCitation: s(r.nominatorCitation), nominatorComments: s(r.nominatorComments),
        managerName: s(r.managerName), managerId: s(r.managerId),
        managerEmail: s(r.managerEmail), managerComments: s(r.managerComments), managerVote: s(r.managerVote),
        firstLevelApproverName: s(r.firstLevelApproverName), firstLevelApproverEmployeeId: s(r.firstLevelApproverEmployeeId),
        firstLevelApproverEmail: s(r.firstLevelApproverEmail), firstLevelApproverUnit: s(r.firstLevelApproverUnit),
        firstLevelApproverComments: s(r.firstLevelApproverComments), firstLevelApproverVote: s(r.firstLevelApproverVote),
        secondLevelApproverName: s(r.secondLevelApproverName), secondLevelApproverEmailId: s(r.secondLevelApproverEmailId),
        secondLevelApproverComments: s(r.secondLevelApproverComments), secondLevelApproverVote: s(r.secondLevelApproverVote),
        eltAverageScore: s(r.eltAverageScore),
        finalLevelApproverName: s(r.finalLevelApproverName), finalLevelApproverEmail: s(r.finalLevelApproverEmail),
        finalLevelApproverComments: s(r.finalLevelApproverComments), finalLevelApproverVote: s(r.finalLevelApproverVote),
        formFields: s(r.formFields), campaignName: s(r.campaignName),
        elt1Name: s(r.elt1Name), elt1Email: s(r.elt1Email), elt1Response: s(r.elt1Response), elt1Score: s(r.elt1Score),
        elt2Name: s(r.elt2Name), elt2Email: s(r.elt2Email), elt2Response: s(r.elt2Response), elt2Score: s(r.elt2Score),
        elt3Name: s(r.elt3Name), elt3Email: s(r.elt3Email), elt3Response: s(r.elt3Response), elt3Score: s(r.elt3Score),
        elt4Name: s(r.elt4Name), elt4Email: s(r.elt4Email), elt4Response: s(r.elt4Response), elt4Score: s(r.elt4Score),
        elt5Name: s(r.elt5Name), elt5Email: s(r.elt5Email), elt5Response: s(r.elt5Response), elt5Score: s(r.elt5Score),
        elt6Name: s(r.elt6Name), elt6Email: s(r.elt6Email), elt6Response: s(r.elt6Response), elt6Score: s(r.elt6Score),
        elt7Name: s(r.elt7Name), elt7Email: s(r.elt7Email), elt7Response: s(r.elt7Response), elt7Score: s(r.elt7Score),
        elt8Name: s(r.elt8Name), elt8Email: s(r.elt8Email), elt8Response: s(r.elt8Response), elt8Score: s(r.elt8Score),
        elt9Name: s(r.elt9Name), elt9Email: s(r.elt9Email), elt9Response: s(r.elt9Response), elt9Score: s(r.elt9Score),
        elt10Name: s(r.elt10Name), elt10Email: s(r.elt10Email), elt10Response: s(r.elt10Response), elt10Score: s(r.elt10Score),
        elt11Name: s(r.elt11Name), elt11Email: s(r.elt11Email), elt11Response: s(r.elt11Response), elt11Score: s(r.elt11Score),
        elt12Name: s(r.elt12Name), elt12Email: s(r.elt12Email), elt12Response: s(r.elt12Response), elt12Score: s(r.elt12Score),
      }));
      setRecords(rows);
      setFiltered(rows);
    } catch {
      message.error('Failed to load awards.');
    } finally {
      setLoading(false);
    }
  }, [fetchToken]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(records.filter(r => {
      const matchSearch = !q ||
        r.nomineeName.toLowerCase().includes(q) ||
        r.nomineeEmail.toLowerCase().includes(q) ||
        r.nominatorName.toLowerCase().includes(q) ||
        r.approvalId.toLowerCase().includes(q) ||
        r.teamName.toLowerCase().includes(q);
      const matchReward = !rewardFilter || r.rewardName === rewardFilter;
      return matchSearch && matchReward;
    }));
    setCurrentPage(1);
  }, [search, rewardFilter, records]);

  const openEdit = (record: AwardRecord) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values: any;
    try { values = await form.validateFields(); }
    catch { return; }
    setSaving(true);
    try {
      const token = await fetchToken();
      const res = await fetch(`${BASE}/api/nominations/awards/${editing!.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(await res.text());
      message.success('Award updated successfully.');
      setModalOpen(false);
      loadRecords();
    } catch (err: any) {
      message.error(err.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const rewardTypes = records.map(r => r.rewardName).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const isTeam = editing?.rewardName?.toLowerCase().includes('team');

  const columns = [
    {
      title: '#', width: 55,
      render: (_: any, __: any, index: number) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{(currentPage - 1) * pageSize + index + 1}</Text>
      ),
    },
    {
      title: 'Approval ID', dataIndex: 'approvalId', key: 'approvalId', width: 110,
      render: (val: string) => <Text style={{ fontSize: 12, fontWeight: 600 }}>{val}</Text>,
    },
    {
      title: 'Award Type', dataIndex: 'rewardName', key: 'rewardName', width: 160,
      render: (val: string) => (
        <Tag color={val?.toLowerCase().includes('team') ? 'blue' : 'purple'} style={{ fontWeight: 600 }}>{val}</Tag>
      ),
    },
    {
      title: <Space><UserOutlined /> Nominee</Space>, dataIndex: 'nomineeName', key: 'nomineeName',
      sorter: (a: AwardRecord, b: AwardRecord) => a.nomineeName.localeCompare(b.nomineeName),
      render: (val: string, r: AwardRecord) => (
        <div>
          <Text style={{ fontSize: 13 }}>{val || r.teamName}</Text><br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.nomineeEmail}</Text>
        </div>
      ),
    },
    {
      title: 'Unit', dataIndex: 'nomineeUnit', key: 'nomineeUnit', width: 130,
      render: (val: string) => <Text style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: <Space><UserOutlined /> Nominator</Space>, dataIndex: 'nominatorName', key: 'nominatorName',
      sorter: (a: AwardRecord, b: AwardRecord) => a.nominatorName.localeCompare(b.nominatorName),
      render: (val: string, r: AwardRecord) => (
        <div>
          <Text style={{ fontSize: 13 }}>{val}</Text><br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.nominatorEmail}</Text>
        </div>
      ),
    },
    {
      title: 'Approval Status', key: 'votes', width: 200,
      render: (_: any, r: AwardRecord) => (
        <Space direction="vertical" size={2}>
          {[['Manager', r.managerVote], ['Unit Head', r.firstLevelApproverVote], ['ELT', r.secondLevelApproverVote], ['Final', r.finalLevelApproverVote]].map(([label, vote]) => (
            <Space key={label as string} size={4}>
              <Text style={{ fontSize: 11, color: '#888', minWidth: 60 }}>{label}:</Text>
              <Badge status={voteColor(vote as string) as any} text={<Text style={{ fontSize: 11 }}>{(vote as string) || 'Pending'}</Text>} />
            </Space>
          ))}
        </Space>
      ),
    },
    {
      title: 'Actions', key: 'actions', width: 80,
      render: (_: any, record: AwardRecord) => (
        <Tooltip title="Edit">
          <Button type="text" icon={<EditOutlined />} size="small" style={{ color: '#8629A4' }} onClick={() => openEdit(record)} />
        </Tooltip>
      ),
    },
  ];

  const tabItems = [
    {
      key: '1', label: 'Basic Info',
      children: (
        <Form.Item name="rewardName" label="Award Type">
          <Input disabled style={{ background: 'rgba(134,41,164,0.04)', color: '#333', fontWeight: 600 }} />
        </Form.Item>
      ),
    },
    {
      key: '2', label: isTeam ? 'Team' : 'Nominee',
      children: isTeam ? (
        <>
          <SectionHeader title="Team Details" />
          <Form.Item name="teamName" label="Team Name"><Input prefix={<TeamOutlined style={{ color: '#bbb' }} />} /></Form.Item>
          <Form.Item name="teamMembers" label="Team Members"><TextArea rows={4} /></Form.Item>
        </>
      ) : (
        <>
          <SectionHeader title="Nominee Details" />
          <G>
            <Form.Item name="nomineeName" label="Name"><Input /></Form.Item>
            <Form.Item name="nomineeEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="nomineeEmployeeId" label="Employee ID"><Input /></Form.Item>
            <Form.Item name="nomineeUnit" label="Unit"><Input /></Form.Item>
            <Form.Item name="nomineeDesignation" label="Designation"><Input /></Form.Item>
            <Form.Item name="nomineeGrade" label="Grade"><Input /></Form.Item>
            <Form.Item name="nomineeCountry" label="Country"><Input /></Form.Item>
            <Form.Item name="nomineeCity" label="City"><Input /></Form.Item>
            <Form.Item name="nomineeLegalEntityName" label="Legal Entity"><Input /></Form.Item>
            <Form.Item name="nomineeBu" label="Business Unit"><Input /></Form.Item>
            <Form.Item name="nomineeProductName" label="Product Name"><Input /></Form.Item>
          </G>
        </>
      ),
    },
    {
      key: '3', label: 'Nominator',
      children: (
        <>
          <SectionHeader title="Nominator Details" />
          <G>
            <Form.Item name="nominatorName" label="Name"><Input /></Form.Item>
            <Form.Item name="nominatorEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="nominatorEmployeeId" label="Employee ID"><Input /></Form.Item>
            <Form.Item name="nominatorUnit" label="Unit"><Input /></Form.Item>
            <Form.Item name="nominatorDesignation" label="Designation"><Input /></Form.Item>
            <Form.Item name="nominatorGrade" label="Grade"><Input /></Form.Item>
            <Form.Item name="nominatorCountry" label="Country"><Input /></Form.Item>
            <Form.Item name="nominatorCity" label="City"><Input /></Form.Item>
            <Form.Item name="nominatorLegalEntityName" label="Legal Entity"><Input /></Form.Item>
            <Form.Item name="nominatorProductName" label="Product Name"><Input /></Form.Item>
          </G>
          <Form.Item name="nominatorCitation" label="Citation"><TextArea rows={3} /></Form.Item>
          <Form.Item name="nominatorComments" label="Comments"><TextArea rows={2} /></Form.Item>
        </>
      ),
    },
    {
      key: '4', label: 'Manager',
      children: (
        <>
          <SectionHeader title="Manager" />
          <G>
            <Form.Item name="managerName" label="Name"><Input /></Form.Item>
            <Form.Item name="managerId" label="Manager ID"><Input /></Form.Item>
            <Form.Item name="managerEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="managerVote" label="Vote"><Select options={VOTE_OPTIONS} /></Form.Item>
          </G>
          <Form.Item name="managerComments" label="Comments"><TextArea rows={2} /></Form.Item>
        </>
      ),
    },
    {
      key: '5', label: 'Approvers',
      children: (
        <>
          <SectionHeader title="Unit Head (Level 1)" />
          <G>
            <Form.Item name="firstLevelApproverName" label="Name"><Input /></Form.Item>
            <Form.Item name="firstLevelApproverEmployeeId" label="Employee ID"><Input /></Form.Item>
            <Form.Item name="firstLevelApproverEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="firstLevelApproverUnit" label="Unit"><Input /></Form.Item>
            <Form.Item name="firstLevelApproverVote" label="Vote"><Select options={VOTE_OPTIONS} /></Form.Item>
          </G>
          <Form.Item name="firstLevelApproverComments" label="Comments"><TextArea rows={2} /></Form.Item>

          <SectionHeader title="ELT Reviewer (Level 2)" />
          <G>
            <Form.Item name="secondLevelApproverName" label="Name"><Input /></Form.Item>
            <Form.Item name="secondLevelApproverEmailId" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="secondLevelApproverVote" label="Vote"><Select options={VOTE_OPTIONS} /></Form.Item>
            <Form.Item name="eltAverageScore" label="ELT Average Score"><Input /></Form.Item>
          </G>
          <Form.Item name="secondLevelApproverComments" label="Comments"><TextArea rows={2} /></Form.Item>

          <SectionHeader title="Final Level Approver" />
          <G>
            <Form.Item name="finalLevelApproverName" label="Name"><Input /></Form.Item>
            <Form.Item name="finalLevelApproverEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="finalLevelApproverVote" label="Vote"><Select options={VOTE_OPTIONS} /></Form.Item>
          </G>
          <Form.Item name="finalLevelApproverComments" label="Comments"><TextArea rows={2} /></Form.Item>
        </>
      ),
    },
    {
      key: '6', label: 'ELT Calibration',
      children: (
        <>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
            <div key={n}>
              <SectionHeader title={`ELT Member ${n}`} />
              <G>
                <Form.Item name={`elt${n}Name`} label="Name"><Input /></Form.Item>
                <Form.Item name={`elt${n}Email`} label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
                <Form.Item name={`elt${n}Response`} label="Response"><Select options={ELT_RESPONSE_OPTIONS} /></Form.Item>
                <Form.Item name={`elt${n}Score`} label="Score"><Input /></Form.Item>
              </G>
            </div>
          ))}
        </>
      ),
    },
    {
      key: '7', label: 'Other',
      children: (
        <>
          <Form.Item name="campaignName" label="Campaign Name"><Input /></Form.Item>
          <Form.Item name="formFields" label="Form Fields"><TextArea rows={4} /></Form.Item>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space wrap>
          <Input.Search
            placeholder="Search nominee, nominator, approval ID…"
            allowClear style={{ width: 300 }}
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <Select
            placeholder="Filter by award type" allowClear style={{ width: 200 }}
            value={rewardFilter || undefined} onChange={val => setRewardFilter(val ?? '')}
            options={rewardTypes.map(t => ({ value: t, label: t }))}
          />
        </Space>
        <Space>
          <Text type="secondary" style={{ fontSize: 13 }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</Text>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={loadRecords} loading={loading} />
          </Tooltip>
        </Space>
      </div>

      <Table
        rowKey="id" columns={columns} dataSource={filtered} loading={loading}
        pagination={{
          current: currentPage, pageSize, showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: t => `Total ${t} records`,
          onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
        }}
        bordered size="middle"
        style={{ borderRadius: 8, overflow: 'hidden' }}
        rowClassName={(_, index) => index % 2 === 0 ? '' : 'ant-table-row-alt'}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: '#8629A4' }} />
            <span>Edit Award — <Text style={{ color: '#8629A4' }}>#{editing?.approvalId}</Text></span>
          </Space>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Update"
        confirmLoading={saving}
        okButtonProps={{ style: { background: '#8629A4', borderColor: '#8629A4' } }}
        destroyOnClose
        width={800}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 } }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Tabs items={tabItems} size="small" />
        </Form>
      </Modal>
    </div>
  );
};

export default AceAviaAwards;
